import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { SettingsAPI, PaymentLinkAPI } from '../services/api';
import { ProcessorAPI } from '../services/api';
import { ProcessorSettings, AgentProcessorRouting, AgentProcessorRoutingUpdate } from '../types/settings';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/rolePermissions';
import { AlertService } from '../services/AlertService';
import { useForm } from 'react-hook-form';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { TFNSettings } from '../components/TFNSettings';
import { ProviderLogo, LogoPicker } from '../components/ProviderLogo';

interface SettingsFormData {
  testModeEnabled: boolean;
  threeDsEnabled: boolean;
  useQuickclick: boolean;
}

interface Provider {
  id: string;
  name: string;
  domain: string;
  description?: string;
  is_active: boolean;
  created_by_name?: string;
  updated_by_name?: string;
  has_logo?: boolean;
  created_at: string;
  updated_at: string;
}

interface ProviderFormData {
  name: string;
  domain: string;
  description: string;
}

interface BulkProviderData {
  providers: string;
  domain: string;
}

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<ProcessorSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Provider management state
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [showCreateProvider, setShowCreateProvider] = useState(false);
  const [showBulkAddProvider, setShowBulkAddProvider] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [providerToDelete, setProviderToDelete] = useState<Provider | null>(null);
  const [providerFilter, setProviderFilter] = useState<string>('');
  // Logo for the create/edit modal: a newly picked file, or a request to remove the saved one.
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [providerSaving, setProviderSaving] = useState(false);
  const resetLogo = () => { setLogoFile(null); setRemoveLogo(false); };

  // Agent Processor Routing state
  const [agentRoutings, setAgentRoutings] = useState<AgentProcessorRouting[]>([]);
  const [dbProcessors, setDbProcessors] = useState<any[]>([]);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingSaving, setRoutingSaving] = useState<string | null>(null); // agent_id being saved
  const [routingSearchFilter, setRoutingSearchFilter] = useState('');
  const [routingEdits, setRoutingEdits] = useState<Record<string, AgentProcessorRoutingUpdate>>({});
  // QuickClick is on: one processor per agent (the per-card routing above is hidden).
  const [qcOptions, setQcOptions] = useState<{ account: string; name: string }[]>([]);
  const [qcEdits, setQcEdits] = useState<Record<string, string>>({});

  const canAccessSettings = hasPermission(user?.role, 'canManageSettings') || 
    ['admin', 'director', 'floor_manager', 'sales_team_lead'].includes(user?.role || '');

  const canManageProviders = !user?.view_only && ['admin', 'director', 'floor_manager', 'sales_team_lead'].includes(user?.role || '');
  // The Use QuickClick toggle is admin-only.
  const isAdmin = user?.role === 'admin';

  const { register, handleSubmit, watch, reset, formState: { isDirty } } = useForm<SettingsFormData>({
    defaultValues: {
      testModeEnabled: false,
      threeDsEnabled: true,
      useQuickclick: false
    }
  });

  // Provider form
  const { 
    register: registerProvider, 
    handleSubmit: handleProviderSubmit, 
    reset: resetProvider, 
    formState: { errors: providerErrors } 
  } = useForm<ProviderFormData>({
    defaultValues: {
      name: '',
      domain: 'Electricity',
      description: ''
    }
  });

  // Bulk provider form
  const { 
    register: registerBulkProvider, 
    handleSubmit: handleBulkProviderSubmit, 
    reset: resetBulkProvider, 
    formState: { errors: bulkProviderErrors } 
  } = useForm<BulkProviderData>({
    defaultValues: {
      providers: '',
      domain: 'Electricity'
    }
  });

  const testModeEnabled = watch('testModeEnabled');
  const threeDsEnabled = watch('threeDsEnabled');
  const useQuickclick = watch('useQuickclick');

  const loadSettings = useCallback(async () => {
    try {
      const processorSettings = await SettingsAPI.getProcessorSettings();
      setSettings(processorSettings);
      reset({
        testModeEnabled: processorSettings.testModeEnabled,
        threeDsEnabled: processorSettings.threeDsEnabled,
        useQuickclick: !!processorSettings.useQuickclick
      });
    } catch (error: any) {
      console.error('Failed to load settings:', error);
      AlertService.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [reset]);

  // Provider management functions
  const loadProviders = useCallback(async () => {
    try {
      setProvidersLoading(true);
      // The endpoint is paginated (max 500/page). Fetch every page using the
      // true total_count so providers past the first page aren't silently
      // dropped from the management list.
      const pageSize = 500;
      const first = await SettingsAPI.getProviders({ limit: pageSize, offset: 0 });
      let all: any[] = first.providers || [];
      const total: number = first.total_count ?? all.length;
      while (all.length < total) {
        const next = await SettingsAPI.getProviders({ limit: pageSize, offset: all.length });
        const batch: any[] = next.providers || [];
        if (batch.length === 0) break; // guard against a bad total_count
        all = all.concat(batch);
      }
      setProviders(all);
    } catch (error: any) {
      console.error('Failed to load providers:', error);
      AlertService.error('Failed to load providers');
      setProviders([]);
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  // Agent Processor Routing functions
  const loadAgentRoutings = useCallback(async () => {
    try {
      setRoutingLoading(true);
      const response = await SettingsAPI.getAgentRoutings();
      setAgentRoutings(response.agents || []);
    } catch (error: any) {
      console.error('Failed to load agent routings:', error);
      setAgentRoutings([]);
    } finally {
      setRoutingLoading(false);
    }
  }, []);

  const loadDbProcessors = useCallback(async () => {
    try {
      const procs = await ProcessorAPI.getDbProcessors();
      setDbProcessors(procs || []);
    } catch (error: any) {
      console.error('Failed to load DB processors:', error);
      setDbProcessors([]);
    }
  }, []);

  const qcValue = (agent: AgentProcessorRouting): string =>
    qcEdits[agent.agentId] !== undefined ? qcEdits[agent.agentId] : (agent.quickclickAccount || '');

  const saveAgentQuickclick = async (agent: AgentProcessorRouting) => {
    try {
      setRoutingSaving(agent.agentId);
      await SettingsAPI.updateAgentQuickclick(agent.agentId, qcValue(agent) || null);
      AlertService.success(`Processor updated for ${agent.agentName || agent.agentUsername}`);
      setQcEdits(({ [agent.agentId]: _, ...rest }) => rest);
      await loadAgentRoutings();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to update the processor');
    } finally {
      setRoutingSaving(null);
    }
  };

  const getRoutingValue = (agent: AgentProcessorRouting, category: string): string => {
    // Check local edits first
    const edits = routingEdits[agent.agentId];
    if (edits) {
      if (category === 'visa' && edits.visaProcessorId !== undefined) return edits.visaProcessorId || '';
      if (category === 'mastercard' && edits.mastercardProcessorId !== undefined) return edits.mastercardProcessorId || '';
      if (category === 'other' && edits.otherProcessorId !== undefined) return edits.otherProcessorId || '';
    }
    // Fall back to server data
    const entry = agent.routings.find(r => r.cardCategory === category);
    return entry?.processorId || '';
  };

  const handleRoutingChange = (agentId: string, category: string, processorId: string) => {
    setRoutingEdits(prev => {
      const agent = agentRoutings.find(a => a.agentId === agentId);
      const agentRoutingEntries = agent?.routings || [];
      const getServer = (cat: string) => agentRoutingEntries.find(r => r.cardCategory === cat)?.processorId || null;

      // Initialize from existing edits, falling back to server values (not null)
      const existing = prev[agentId] || {
        visaProcessorId: getServer('visa'),
        mastercardProcessorId: getServer('mastercard'),
        otherProcessorId: getServer('other'),
      };

      const newValue = processorId || null;
      const updated = { ...existing };

      if (category === 'visa') updated.visaProcessorId = newValue;
      else if (category === 'mastercard') updated.mastercardProcessorId = newValue;
      else if (category === 'other') updated.otherProcessorId = newValue;

      // If ALL values match server, remove edits for this agent
      const isClean =
        (updated.visaProcessorId || null) === (getServer('visa') || null) &&
        (updated.mastercardProcessorId || null) === (getServer('mastercard') || null) &&
        (updated.otherProcessorId || null) === (getServer('other') || null);

      if (isClean) {
        const { [agentId]: _, ...rest } = prev;
        return rest;
      }

      return { ...prev, [agentId]: updated };
    });
  };

  const saveAgentRouting = async (agentId: string) => {
    const agent = agentRoutings.find(a => a.agentId === agentId);
    if (!agent) return;

    const edits = routingEdits[agentId];
    // Build payload using edits if present, else server values
    const getServer = (cat: string) => agent.routings.find(r => r.cardCategory === cat)?.processorId || null;
    const payload: AgentProcessorRoutingUpdate = {
      visaProcessorId: edits?.visaProcessorId !== undefined ? edits.visaProcessorId : getServer('visa'),
      mastercardProcessorId: edits?.mastercardProcessorId !== undefined ? edits.mastercardProcessorId : getServer('mastercard'),
      otherProcessorId: edits?.otherProcessorId !== undefined ? edits.otherProcessorId : getServer('other'),
    };

    try {
      setRoutingSaving(agentId);
      await SettingsAPI.updateAgentRouting(agentId, payload);
      AlertService.success(`Routing updated for ${agent.agentName || agent.agentUsername}`);
      // Clear edits for this agent
      setRoutingEdits(prev => {
        const { [agentId]: _, ...rest } = prev;
        return rest;
      });
      await loadAgentRoutings();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to update routing');
    } finally {
      setRoutingSaving(null);
    }
  };

  useEffect(() => {
    if (canAccessSettings) {
      loadSettings();
      loadAgentRoutings();
      loadDbProcessors();
      PaymentLinkAPI.getQuickclickAccounts().then(setQcOptions).catch(() => setQcOptions([]));
    }
    if (canManageProviders) {
      loadProviders();
    }
  }, [canAccessSettings, canManageProviders, loadSettings, loadProviders, loadAgentRoutings, loadDbProcessors]);

  const onCreateProvider = async (data: ProviderFormData) => {
    setProviderSaving(true);
    try {
      const created = await SettingsAPI.createProvider({
        name: data.name.trim(),
        domain: data.domain,
        description: data.description.trim() || undefined
      });
      let logoError: string | null = null;
      if (logoFile && created?.id) {
        try { await SettingsAPI.uploadProviderLogo(created.id, logoFile); } catch (e: any) { logoError = e.message; }
      }
      if (logoError) AlertService.error(`Provider created, but the logo didn't upload: ${logoError}. Add it via Edit.`);
      else AlertService.success('Provider created successfully');
      setShowCreateProvider(false);
      resetProvider();
      resetLogo();
      loadProviders();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to create provider');
    } finally {
      setProviderSaving(false);
    }
  };

  const onEditProvider = async (data: ProviderFormData) => {
    if (!editingProvider) return;
    setProviderSaving(true);
    try {
      await SettingsAPI.updateProvider(editingProvider.id, {
        name: data.name.trim(),
        description: data.description.trim() || undefined
      });
      let logoError: string | null = null;
      try {
        if (logoFile) await SettingsAPI.uploadProviderLogo(editingProvider.id, logoFile);
        else if (removeLogo && editingProvider.has_logo) await SettingsAPI.removeProviderLogo(editingProvider.id);
      } catch (e: any) { logoError = e.message; }
      if (logoError) AlertService.error(`Provider updated, but the logo change failed: ${logoError}`);
      else AlertService.success('Provider updated successfully');
      setEditingProvider(null);
      setShowCreateProvider(false);
      resetProvider();
      resetLogo();
      loadProviders();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to update provider');
    } finally {
      setProviderSaving(false);
    }
  };

  const onBulkAddProviders = async (data: BulkProviderData) => {
    try {
      const providerNames = data.providers
        .split('\n')
        .map(name => name.trim())
        .filter(name => name.length > 0);

      if (providerNames.length === 0) {
        AlertService.error('Please enter at least one provider name');
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      for (const name of providerNames) {
        try {
          await SettingsAPI.createProvider({
            name: name,
            domain: data.domain,
            description: undefined
          });
          successCount++;
        } catch (error: any) {
          errorCount++;
          errors.push(`${name}: ${error.message || 'Unknown error'}`);
        }
      }

      if (successCount > 0) {
        AlertService.success(`Successfully created ${successCount} provider(s)`);
      }
      
      if (errorCount > 0) {
        AlertService.error(`Failed to create ${errorCount} provider(s). First error: ${errors[0]}`);
      }

      setShowBulkAddProvider(false);
      resetBulkProvider();
      loadProviders();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to bulk add providers');
    }
  };

  const handleDeleteProvider = async () => {
    if (!providerToDelete) return;
    
    try {
      await SettingsAPI.deleteProvider(providerToDelete.id);
      AlertService.success('Provider deleted successfully');
      setProviderToDelete(null);
      loadProviders();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to delete provider');
    }
  };

  const toggleProviderActive = async (provider: Provider) => {
    try {
      await SettingsAPI.updateProvider(provider.id, {
        is_active: !provider.is_active
      });
      AlertService.success(`Provider ${provider.is_active ? 'deactivated' : 'activated'} successfully`);
      loadProviders();
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to update provider status');
    }
  };

  const startEdit = (provider: Provider) => {
    resetLogo();
    setEditingProvider(provider);
    resetProvider({
      name: provider.name,
      domain: provider.domain,
      description: provider.description || ''
    });
    setShowCreateProvider(true);
  };

  const cancelEdit = () => {
    setEditingProvider(null);
    setShowCreateProvider(false);
    resetProvider();
    resetLogo();
  };

  const filteredProviders = providers.filter(provider => {
    if (!providerFilter) return true;
    return (
      provider.name.toLowerCase().includes(providerFilter.toLowerCase()) ||
      provider.domain.toLowerCase().includes(providerFilter.toLowerCase()) ||
      (provider.description && provider.description.toLowerCase().includes(providerFilter.toLowerCase()))
    );
  });

  const onSubmit = async (data: SettingsFormData) => {
    try {
      setSaving(true);
      const updatedSettings = await SettingsAPI.updateProcessorSettings({
        testModeEnabled: data.testModeEnabled,
        threeDsEnabled: data.threeDsEnabled,
        // Admin-only; backend ignores this from non-admins.
        ...(isAdmin ? { useQuickclick: data.useQuickclick } : {})
      });
      setSettings(updatedSettings);

      reset({
        testModeEnabled: updatedSettings.testModeEnabled,
        threeDsEnabled: updatedSettings.threeDsEnabled,
        useQuickclick: !!updatedSettings.useQuickclick
      });

      AlertService.success('Settings updated successfully');
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      AlertService.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      reset({
        testModeEnabled: settings.testModeEnabled,
        threeDsEnabled: settings.threeDsEnabled,
        useQuickclick: !!settings.useQuickclick
      });
    }
  };

  if (!canAccessSettings) {
    return (
      <DashboardLayout>
        <div className="settings">
          <div className="access-denied">
            <h2>Access Denied</h2>
            <p>You don't have permission to access settings.</p>
            <p>Contact your administrator if you believe this is an error.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="settings">
        <div className="page-header">
          <div>
            <h1>Settings</h1>
            <p>Configure system settings and payment processing options</p>
          </div>
        </div>

        {loading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Loading settings...</p>
          </div>
        ) : (
          <div className="settings-container">
            {/* Agent Processor Routing Section */}
            {canAccessSettings && (
              <div className="settings-card agent-routing">
                <div className="card-header">
                  <h3>Agent Processor Routing</h3>
                  <p>{useQuickclick ? "Configure which processor each agent's payment links go through" : 'Configure which processor each agent uses per card type (Visa, MasterCard, Other)'}</p>
                </div>

                <div className="routing-controls" style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    placeholder="Search agents..."
                    value={routingSearchFilter}
                    onChange={(e) => setRoutingSearchFilter(e.target.value)}
                    className="provider-search"
                  />
                </div>

                {routingLoading ? (
                  <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>Loading agent routings...</p>
                ) : agentRoutings.length === 0 ? (
                  <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No routable agents found. Sync processors first.</p>
                ) : (
                  <div className="routing-table-wrapper" style={{ overflowX: 'auto' }}>
                    {useQuickclick && (
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
                        QuickClick is on: each agent's payment links go through the processor set here. Links already created stay on their processor.
                      </p>
                    )}
                    <table className="routing-table">
                      <thead>
                        <tr>
                          <th>Agent</th>
                          <th>Role</th>
                          {useQuickclick ? <th>Processor</th> : (
                            <>
                              <th>Visa</th>
                              <th>MasterCard</th>
                              <th>Other</th>
                            </>
                          )}
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentRoutings
                          .filter(a => {
                            if (!routingSearchFilter) return true;
                            const q = routingSearchFilter.toLowerCase();
                            return (a.agentName || '').toLowerCase().includes(q) ||
                              a.agentUsername.toLowerCase().includes(q) ||
                              a.agentRole.toLowerCase().includes(q);
                          })
                          .map(agent => (
                            <tr key={agent.agentId}>
                              <td style={{ fontWeight: 500 }}>{agent.agentName || agent.agentUsername}</td>
                              <td><span className="role-badge">{agent.agentRole.replace(/_/g, ' ')}</span></td>
                              {useQuickclick && (
                                <td>
                                  <select
                                    className="routing-select"
                                    value={qcValue(agent)}
                                    onChange={(e) => setQcEdits(prev => ({ ...prev, [agent.agentId]: e.target.value }))}
                                  >
                                    <option value="">{qcOptions[0] ? `-- Default (${qcOptions[0].name}) --` : '-- Default --'}</option>
                                    {qcOptions.map(o => <option key={o.account} value={o.account}>{o.name}</option>)}
                                  </select>
                                </td>
                              )}
                              {!useQuickclick && (['visa', 'mastercard', 'other'] as const).map(cat => (
                                <td key={cat}>
                                  <select
                                    className="routing-select"
                                    value={getRoutingValue(agent, cat)}
                                    onChange={(e) => handleRoutingChange(agent.agentId, cat, e.target.value)}
                                  >
                                    <option value="">-- Not configured --</option>
                                    {dbProcessors.filter(p => p.nmi_processor_id).map(p => (
                                      <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                  </select>
                                </td>
                              ))}
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  disabled={routingSaving === agent.agentId || (useQuickclick
                                    ? qcValue(agent) === (agent.quickclickAccount || '')
                                    : !routingEdits[agent.agentId])}
                                  onClick={() => (useQuickclick ? saveAgentQuickclick(agent) : saveAgentRouting(agent.agentId))}
                                >
                                  {routingSaving === agent.agentId ? 'Saving...' : 'Save'}
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {dbProcessors.length === 0 && !routingLoading && (
                  <p style={{ textAlign: 'center', color: 'var(--warning)', padding: '0.5rem', fontSize: '0.85rem' }}>
                    No processors found in DB. Go to Processors &gt; Sync All to import from NMI first.
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="settings-form">
              {/* Processor Settings Card */}
              <div className="settings-card">
                <div className="card-header">
                  <h3>Processor Settings</h3>
                  <p>Control test mode for payment processing</p>
                </div>

                <div className="card-content">
                  {/* Test Mode Toggle */}
                  <div className="setting-group">
                    <div className="setting-header">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          {...register('testModeEnabled')}
                          className="toggle-input"
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-text">
                          {testModeEnabled ? 'Test Mode Active' : 'Test Mode Off'}
                        </span>
                      </label>
                    </div>
                    <div className="setting-description">
                      <p>
                        {testModeEnabled
                          ? 'Test mode is active. All payments use the NMI test account. No real charges.'
                          : 'Payments are processed live using per-agent processor routing.'
                        }
                      </p>
                    </div>
                  </div>

                  {/* 3RI Toggle */}
                  <div className="setting-group">
                    <div className="setting-header">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          {...register('threeDsEnabled')}
                          className="toggle-input"
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-text">
                          {threeDsEnabled ? '3RI Authentication Enabled' : '3RI Authentication Disabled'}
                        </span>
                      </label>
                    </div>
                    <div className="setting-description">
                      <p>
                        {threeDsEnabled
                          ? '3RI authentication runs on agent-initiated sales (Direct Sale & Self-Authorization). It is also the default 3DS state for new payment links, which a supervisor then confirms per link.'
                          : '3RI is off — agents are prompted to confirm before each sale, and new payment links default to skipping 3DS (a supervisor can still turn it on per link).'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Use QuickClick Toggle (admin only) */}
                  {isAdmin && (
                    <div className="setting-group">
                      <div className="setting-header">
                        <label className="toggle-label">
                          <input
                            type="checkbox"
                            {...register('useQuickclick')}
                            className="toggle-input"
                          />
                          <span className="toggle-slider"></span>
                          <span className="toggle-text">
                            {useQuickclick ? 'QuickClick Enabled' : 'QuickClick Disabled'}
                          </span>
                        </label>
                      </div>
                      <div className="setting-description">
                        <p>
                          {useQuickclick
                            ? 'Customer payment links use NMI QuickClick hosted checkout. 3DS is handled account-wide by NMI (per-link 3DS does not apply).'
                            : 'Customer payment links use the built-in payment page (current system).'
                          }
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Current Status Display */}
                  {settings && (
                    <div className="setting-group">
                      <div className="current-status">
                        <h4>Current Status</h4>
                        <div className="status-grid">
                          <div className="status-item">
                            <label>Test Mode:</label>
                            <span className={`status-badge ${settings.testModeEnabled ? 'manual' : 'auto'}`}>
                              {settings.testModeEnabled ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="status-item">
                            <label>3RI:</label>
                            <span className={`status-badge ${settings.threeDsEnabled ? 'auto' : 'manual'}`}>
                              {settings.threeDsEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          <div className="status-item">
                            <label>Last Updated:</label>
                            <span>{new Date(settings.updatedAt).toLocaleString()}</span>
                          </div>
                          <div className="status-item">
                            <label>Updated By:</label>
                            <span>{settings.updatedBy}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="card-actions">
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={!isDirty || saving}
                    className="btn btn-outline-secondary"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={!isDirty || saving}
                    className={`btn ${saving ? 'btn-loading' : 'btn-primary'}`}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </form>

            {/* TFN Management Section */}
            {canManageProviders && (
              <div className="settings-card tfn-management" style={{ marginTop: '2rem' }}>
                <TFNSettings />
              </div>
            )}

            {/* Provider Management Section */}
            {canManageProviders && (
              <div className="settings-card provider-management" style={{ marginTop: '2rem' }}>
                <div className="card-header">
                  <h3>Provider Management</h3>
                  <p>Manage utility providers for domain-based sales</p>
                </div>

                <div className="provider-controls">
                  <div className="search-section">
                    <input
                      type="text"
                      placeholder="Search providers..."
                      value={providerFilter}
                      onChange={(e) => setProviderFilter(e.target.value)}
                      className="provider-search"
                    />
                    <button
                      type="button"
                      onClick={() => { resetLogo(); setShowCreateProvider(true); }}
                      className="btn btn-primary"
                    >
                      Add Provider
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBulkAddProvider(true)}
                      className="btn btn-outline-primary"
                    >
                      Bulk Add Providers
                    </button>
                  </div>

                  {providersLoading ? (
                    <div className="loading">
                      <div className="loading-spinner"></div>
                      <p>Loading providers...</p>
                    </div>
                  ) : filteredProviders.length === 0 ? (
                    <div className="no-providers">
                      <p>No providers found. {providers.length === 0 ? 'Create your first provider!' : 'Try adjusting your search.'}</p>
                    </div>
                  ) : (
                    <div className="providers-grid">
                      {filteredProviders.map(provider => (
                        <div key={provider.id} className={`provider-card ${!provider.is_active ? 'inactive' : ''}`}>
                          <div className="provider-header">
                            <div className="provider-info">
                              {provider.has_logo && (
                                <div className="provider-logo-thumb">
                                  <ProviderLogo providerId={provider.id} version={provider.updated_at} size={36} alt={`${provider.name} logo`} />
                                </div>
                              )}
                              <h4>{provider.name}</h4>
                              <span className="provider-domain">{provider.domain}</span>
                              <span className={`provider-status ${provider.is_active ? 'active' : 'inactive'}`}>
                                {provider.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="provider-actions">
                              <button
                                type="button"
                                onClick={() => toggleProviderActive(provider)}
                                className={`btn btn-sm ${provider.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                title={provider.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {provider.is_active ? '⏸️' : '▶️'}
                              </button>
                              <button
                                type="button"
                                onClick={() => startEdit(provider)}
                                className="btn btn-sm btn-outline-primary"
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                onClick={() => setProviderToDelete(provider)}
                                className="btn btn-sm btn-outline-danger"
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                          {provider.description && (
                            <p className="provider-description">{provider.description}</p>
                          )}
                          <div className="provider-meta">
                            <small>Created by {provider.created_by_name || 'Unknown'} • {new Date(provider.created_at).toLocaleDateString()}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Provider Create/Edit Modal */}
            {showCreateProvider && (
              <div className="modal-overlay">
                <div className="modal">
                  <div className="modal-header">
                    <h3>{editingProvider ? 'Edit Provider' : 'Create Provider'}</h3>
                    <button type="button" onClick={cancelEdit} className="modal-close">×</button>
                  </div>
                  
                  <form onSubmit={handleProviderSubmit(editingProvider ? onEditProvider : onCreateProvider)}>
                    <div className="modal-body">
                      <div className="form-group">
                        <label htmlFor="providerName">Provider Name *</label>
                        <input
                          id="providerName"
                          type="text"
                          {...registerProvider('name', { required: 'Provider name is required' })}
                          className={`form-input ${providerErrors.name ? 'error' : ''}`}
                        />
                        {providerErrors.name && (
                          <span className="error-message">{providerErrors.name.message}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="providerDomain">Domain *</label>
                        <select
                          id="providerDomain"
                          {...registerProvider('domain', { required: 'Domain is required' })}
                          className={`form-input ${providerErrors.domain ? 'error' : ''}`}
                          disabled={editingProvider !== null}
                        >
                          <option value="Electricity">Electricity</option>
                          <option value="Gas">Gas</option>
                          <option value="Water">Water</option>
                        </select>
                        {providerErrors.domain && (
                          <span className="error-message">{providerErrors.domain.message}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="providerDescription">Description</label>
                        <textarea
                          id="providerDescription"
                          {...registerProvider('description')}
                          className="form-input"
                          rows={3}
                          placeholder="Optional description..."
                        />
                      </div>

                      <div className="form-group">
                        {editingProvider?.has_logo && !logoFile && (
                          <div className="provider-logo-current">
                            <label>Current logo</label>
                            {removeLogo ? (
                              <div className="provider-logo-current-row">
                                <span className="provider-logo-removed">The logo will be removed when you save.</span>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setRemoveLogo(false)}>Undo</button>
                              </div>
                            ) : (
                              <div className="provider-logo-current-row">
                                <ProviderLogo providerId={editingProvider.id} version={editingProvider.updated_at} size={44} alt={`${editingProvider.name} logo`} />
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setRemoveLogo(true)}>Remove logo</button>
                              </div>
                            )}
                          </div>
                        )}
                        <LogoPicker
                          file={logoFile}
                          onChange={(f) => { setLogoFile(f); if (f) setRemoveLogo(false); }}
                          disabled={providerSaving}
                          label={editingProvider?.has_logo ? 'Replace logo' : 'Logo (optional)'}
                        />
                      </div>
                    </div>

                    <div className="modal-footer">
                      <button type="button" onClick={cancelEdit} className="btn btn-outline-secondary">
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={providerSaving}>
                        {providerSaving ? 'Saving…' : editingProvider ? 'Update Provider' : 'Create Provider'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Bulk Add Provider Modal */}
            {showBulkAddProvider && (
              <div className="modal-overlay">
                <div className="modal">
                  <div className="modal-header">
                    <h3>Bulk Add Providers</h3>
                    <button type="button" onClick={() => setShowBulkAddProvider(false)} className="modal-close">×</button>
                  </div>
                  
                  <form onSubmit={handleBulkProviderSubmit(onBulkAddProviders)}>
                    <div className="modal-body">
                      <div className="form-group">
                        <label htmlFor="bulkDomain">Domain *</label>
                        <select
                          id="bulkDomain"
                          {...registerBulkProvider('domain', { required: 'Domain is required' })}
                          className={`form-input ${bulkProviderErrors.domain ? 'error' : ''}`}
                        >
                          <option value="Electricity">Electricity</option>
                          <option value="Gas">Gas</option>
                          <option value="Water">Water</option>
                        </select>
                        {bulkProviderErrors.domain && (
                          <span className="error-message">{bulkProviderErrors.domain.message}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="bulkProviders">Provider Names *</label>
                        <textarea
                          id="bulkProviders"
                          {...registerBulkProvider('providers', { required: 'Please enter at least one provider name' })}
                          className={`form-input ${bulkProviderErrors.providers ? 'error' : ''}`}
                          rows={8}
                          placeholder="Enter one provider name per line, for example:&#10;&#10;National Grid&#10;ConEd&#10;PSEG&#10;Eversource"
                        />
                        {bulkProviderErrors.providers && (
                          <span className="error-message">{bulkProviderErrors.providers.message}</span>
                        )}
                      </div>

                      <div className="help-text">
                        <p><strong>Instructions:</strong></p>
                        <ul>
                          <li>Enter one provider name per line</li>
                          <li>Empty lines will be ignored</li>
                          <li>Leading/trailing spaces will be trimmed</li>
                          <li>All providers will be created for the selected domain</li>
                          <li>Duplicate names will show an error but won't stop other providers from being created</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="modal-footer">
                      <button type="button" onClick={() => setShowBulkAddProvider(false)} className="btn btn-outline-secondary">
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary">
                        Add All Providers
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {providerToDelete && (
          <ConfirmationDialog
            isOpen={true}
            title="Delete Provider"
            message={`Are you sure you want to delete "${providerToDelete.name}"? This action cannot be undone.`}
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={handleDeleteProvider}
            onCancel={() => setProviderToDelete(null)}
            confirmButtonClass="btn-danger"
          />
        )}
      </div>

      <style>{`
        .settings {
          max-width: 800px;
          margin: 0 auto;
          padding: 1rem;
        }

        .page-header {
          margin-bottom: 2rem;
        }

        .page-header h1 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 2rem;
          font-weight: 600;
        }

        .page-header p {
          margin: 0;
          color: var(--text-secondary);
        }

        .loading {
          text-align: center;
          padding: 4rem 2rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--border-primary);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .access-denied {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--bg-card);
          border-radius: 8px;
        }

        .access-denied h2 {
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .access-denied p {
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }

        .settings-container {
          background: var(--bg-card);
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-primary);
        }

        .settings-form {
          padding: 0;
        }

        .settings-card {
          padding: 2rem;
        }

        .card-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--border-primary);
        }

        .card-header h3 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 1.5rem;
          font-weight: 600;
        }

        .card-header p {
          margin: 0;
          color: var(--text-secondary);
        }

        .card-content {
          margin-bottom: 2rem;
        }

        .setting-group {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 8px;
          border: 1px solid var(--border-primary);
        }

        .setting-group:last-child {
          margin-bottom: 0;
        }

        .setting-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .setting-label {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 1rem;
        }

        .setting-badge {
          padding: 0.25rem 0.5rem;
          background: var(--primary-light);
          color: var(--primary);
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        /* Toggle Switch Styles */
        .toggle-label {
          display: flex;
          align-items: center;
          gap: 1rem;
          cursor: pointer;
          font-weight: 600;
          color: var(--text-primary);
        }

        .toggle-input {
          display: none;
        }

        .toggle-slider {
          position: relative;
          width: 60px;
          height: 34px;
          background: var(--border-secondary);
          border-radius: 34px;
          transition: background 0.3s;
        }

        .toggle-slider::before {
          content: '';
          position: absolute;
          top: 4px;
          left: 4px;
          width: 26px;
          height: 26px;
          background: white;
          border-radius: 50%;
          transition: transform 0.3s;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .toggle-input:checked + .toggle-slider {
          background: var(--primary);
        }

        .toggle-input:checked + .toggle-slider::before {
          transform: translateX(26px);
        }

        .toggle-text {
          font-size: 1.125rem;
        }

        /* Select Styles */
        .setting-select {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.9375rem;
          margin-bottom: 0.5rem;
        }

        .setting-select:focus {
          outline: 2px solid var(--border-focus);
          outline-offset: 2px;
          border-color: var(--primary);
        }

        .setting-select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .setting-description {
          color: var(--text-secondary);
          font-size: 0.875rem;
          line-height: 1.5;
        }

        .setting-description p {
          margin: 0;
        }

        /* Current Status */
        .current-status {
          background: var(--bg-primary);
          padding: 1rem;
          border-radius: 6px;
          border: 1px solid var(--border-primary);
        }

        .current-status h4 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-size: 1rem;
          font-weight: 600;
        }

        .status-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .status-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          align-items: flex-start;
        }

        .status-item label {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .status-item span {
          font-size: 0.875rem;
          color: var(--text-primary);
        }

        .status-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          width: fit-content;
          align-self: flex-start;
        }

        .status-badge.auto {
          background: var(--success-light);
          color: var(--success);
        }

        .status-badge.manual {
          background: var(--warning-light);
          color: var(--warning);
        }

        .processor-name {
          font-weight: 500;
          color: var(--primary);
        }

        /* Card Actions */
        .card-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--border-primary);
        }

        /* Provider Management Styles */
        .provider-controls {
          margin: 2rem 0;
        }

        .search-section {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          align-items: center;
        }

        .provider-search {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          background: var(--input-bg);
          color: var(--text-primary);
          font-size: 0.9375rem;
        }

        .provider-search:focus {
          outline: 2px solid var(--border-focus);
          outline-offset: 2px;
          border-color: var(--input-focus);
        }

        .providers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
        }

        .provider-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          padding: 1rem;
          transition: all 0.2s ease;
        }

        .provider-card:hover {
          border-color: var(--primary);
          box-shadow: 0 4px 12px var(--shadow-medium);
        }

        .provider-card.inactive {
          opacity: 0.7;
        }

        .provider-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.5rem;
        }

        .provider-logo-thumb {
          margin-bottom: 0.4rem;
          display: inline-block;
          background: #fff;
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          padding: 3px 6px;
        }

        .provider-logo-current {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 0.75rem;
        }

        .provider-logo-current-row {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .provider-logo-removed {
          font-size: 0.8rem;
          color: var(--danger);
        }

        .provider-info h4 {
          margin: 0 0 0.25rem 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .provider-domain {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          background: var(--primary-light);
          color: var(--primary);
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          margin-right: 0.5rem;
        }

        .provider-status {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .provider-status.active {
          background: var(--success-light);
          color: var(--success);
        }

        .provider-status.inactive {
          background: var(--danger-light);
          color: var(--danger);
        }

        .provider-actions {
          display: flex;
          gap: 0.5rem;
        }

        .provider-description {
          margin: 0.75rem 0 0.5rem 0;
          font-size: 0.875rem;
          color: var(--text-secondary);
          line-height: 1.4;
        }

        .provider-meta {
          margin-top: 0.75rem;
          padding-top: 0.5rem;
          border-top: 1px solid var(--border-primary);
        }

        .provider-meta small {
          color: var(--text-muted);
          font-size: 0.75rem;
        }

        .no-providers {
          text-align: center;
          padding: 3rem 1rem;
          color: var(--text-secondary);
          background: var(--bg-secondary);
          border-radius: 8px;
          border: 1px dashed var(--border-primary);
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: var(--bg-card);
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 10px 25px var(--shadow-heavy);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid var(--border-primary);
        }

        .modal-header h3 {
          margin: 0;
          color: var(--text-primary);
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--text-secondary);
          padding: 0;
          width: 2rem;
          height: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }

        .modal-close:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .modal-body {
          padding: 1.5rem;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          padding: 1rem 1.5rem 1.5rem;
          border-top: 1px solid var(--border-primary);
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--text-primary);
        }

        .form-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--input-border);
          border-radius: 6px;
          background: var(--input-bg);
          color: var(--text-primary);
          font-size: 0.9375rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--input-focus);
          box-shadow: var(--shadow-focus);
        }

        .form-input.error {
          border-color: var(--input-error);
        }

        .error-message {
          display: block;
          margin-top: 0.25rem;
          font-size: 0.75rem;
          color: var(--input-error);
        }

        textarea.form-input {
          resize: vertical;
          min-height: 80px;
        }

        .btn-outline-success {
          background-color: transparent;
          border-color: var(--success);
          color: var(--success);
        }

        .btn-outline-success:hover:not(:disabled) {
          background-color: var(--success);
          border-color: var(--success);
          color: var(--text-inverse);
          transform: translateY(-1px);
        }

        .btn-outline-warning {
          background-color: transparent;
          border-color: var(--warning);
          color: var(--warning);
        }

        .btn-outline-warning:hover:not(:disabled) {
          background-color: var(--warning);
          border-color: var(--warning);
          color: var(--text-primary);
          transform: translateY(-1px);
        }

        .btn-outline-danger {
          background-color: transparent;
          border-color: var(--danger);
          color: var(--danger);
        }

        .btn-outline-danger:hover:not(:disabled) {
          background-color: var(--danger);
          border-color: var(--danger);
          color: var(--text-inverse);
          transform: translateY(-1px);
        }

        @media (max-width: 768px) {
          .settings {
            padding: 0.5rem;
          }

          .settings-card {
            padding: 1rem;
          }

          .setting-group {
            padding: 1rem;
          }

          .status-grid {
            grid-template-columns: 1fr;
          }

          .card-actions {
            flex-direction: column-reverse;
          }

          .toggle-label {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .providers-grid {
            grid-template-columns: 1fr;
          }

          .search-section {
            flex-direction: column;
            align-items: stretch;
          }

          .modal {
            width: 95%;
            max-height: 95vh;
          }

          .modal-header, .modal-body, .modal-footer {
            padding: 1rem;
          }
        }

        /* Agent Processor Routing Styles */
        .routing-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .routing-table th {
          text-align: left;
          padding: 0.75rem 0.5rem;
          border-bottom: 2px solid var(--border-primary);
          color: var(--text-secondary);
          font-weight: 600;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .routing-table td {
          padding: 0.5rem;
          border-bottom: 1px solid var(--border-primary);
          vertical-align: middle;
        }

        .routing-table tbody tr:hover {
          background: var(--bg-secondary);
        }

        .routing-select {
          width: 100%;
          min-width: 140px;
          padding: 0.4rem 0.5rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.8125rem;
        }

        .routing-select:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px rgba(var(--primary-rgb, 59, 130, 246), 0.15);
        }

        .role-badge {
          display: inline-block;
          padding: 0.15rem 0.5rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 10px;
          font-size: 0.75rem;
          text-transform: capitalize;
          color: var(--text-secondary);
        }

        .btn-sm {
          padding: 0.35rem 0.75rem;
          font-size: 0.8rem;
        }
      `}</style>
    </DashboardLayout>
  );
};